import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, 
  X, 
  Send, 
  MessageSquare, 
  History, 
  Trash2, 
  ChevronLeft,
  Loader2,
  Bot,
  User,
  Lightbulb,
  Search,
  FileText,
  Zap,
  HelpCircle,
  Target,
  LayoutDashboard,
  Users,
  Briefcase,
  Heart,
  Calculator,
  DollarSign,
  TrendingUp,
  Calendar,
  Tag,
  Edit,
  MessageCircle,
  Star,
  Gift,
  BarChart3,
  AlertCircle,
  Wand2,
  Copy,
  Check,
  RotateCcw,
  PanelRightClose,
  Compass,
  Plus,
  Trash,
  Mail,
  Play,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Command,
  ChevronRight,
  Mic,
  MicOff,
  Image as ImageIcon,
  Download,
  Palette,
  Maximize2,
  Minimize2,
  Database,
  Globe,
  GraduationCap,
  Settings,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthToken } from '../App';

// Icon mapping for suggested prompts and actions
const PROMPT_ICONS = {
  help: HelpCircle,
  lightbulb: Lightbulb,
  search: Search,
  sparkles: Sparkles,
  users: Users,
  folder: LayoutDashboard,
  check: Check,
  file: FileText,
  message: MessageCircle,
  edit: Edit,
  list: FileText,
  star: Star,
  filter: Search,
  calendar: Calendar,
  tag: Tag,
  alert: AlertCircle,
  heart: Heart,
  chart: BarChart3,
  trending: TrendingUp,
  calculator: Calculator,
  dollar: DollarSign,
  gift: Gift,
  layout: LayoutDashboard,
  target: Target,
  zap: Zap,
  workflow: Briefcase,
  rocket: Sparkles,
  compass: Compass,
  plus: Plus,
  trash: Trash,
  mail: Mail,
  'message-circle': MessageCircle
};

// Action type icons
const ACTION_ICONS = {
  navigate: Compass,
  search: Search,
  create: Plus,
  update: Edit,
  delete: Trash,
  email: Mail,
  message: MessageCircle,
  workflow: Zap,
  image: Palette
};

// Action type colors
const ACTION_COLORS = {
  navigate: 'bg-blue-500',
  search: 'bg-emerald-500',
  create: 'bg-green-500',
  update: 'bg-amber-500',
  delete: 'bg-red-500',
  email: 'bg-purple-500',
  message: 'bg-indigo-500',
  workflow: 'bg-orange-500',
  image: 'bg-pink-500'
};

// Format message with markdown-like styling
const formatAIMessage = (content) => {
  if (!content) return content;
  
  // Convert markdown bold to HTML
  let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Convert markdown italic
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Convert bullet points
  formatted = formatted.replace(/^- /gm, '• ');
  // Convert numbered lists
  formatted = formatted.replace(/^(\d+)\. /gm, '$1. ');
  
  return formatted;
};

// Action Confirmation Card Component
const ActionConfirmCard = ({ action, onExecute, onCancel, isExecuting }) => {
  const ActionIcon = ACTION_ICONS[action.action_type] || Zap;
  const colorClass = ACTION_COLORS[action.action_type] || 'bg-violet-500';
  
  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl border-2 border-violet-200 dark:border-violet-800 p-4 my-3 shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center shadow-lg`}>
          <ActionIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-white">
              {action.action_type.charAt(0).toUpperCase() + action.action_type.slice(1)} Action
            </span>
            <Badge variant="outline" className="text-xs capitalize">
              {action.target}
            </Badge>
          </div>
          <p className="text-xs text-slate-500">Requires confirmation</p>
        </div>
      </div>
      
      {/* Description */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-3 mb-3 border border-slate-200 dark:border-slate-700">
        <p 
          className="text-sm text-slate-700 dark:text-slate-300"
          dangerouslySetInnerHTML={{ __html: formatAIMessage(action.description) }}
        />
      </div>
      
      {/* Parameters Preview */}
      {action.parameters && Object.keys(action.parameters).length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-slate-500 mb-1">Parameters:</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(action.parameters).slice(0, 4).map(([key, value]) => (
              <Badge key={key} variant="secondary" className="text-xs">
                {key}: {typeof value === 'object' ? JSON.stringify(value).slice(0, 20) : String(value).slice(0, 20)}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={onExecute}
          disabled={isExecuting}
          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white gap-2"
          data-testid="execute-action-btn"
        >
          {isExecuting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Execute
            </>
          )}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isExecuting}
          variant="outline"
          className="flex-1 gap-2 border-red-200 text-red-600 hover:bg-red-50"
          data-testid="cancel-action-btn"
        >
          <XCircle className="w-4 h-4" />
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Action Result Card Component
const ActionResultCard = ({ result, actionType }) => {
  const isSuccess = result.success;
  
  // Download image helper
  const downloadImage = (format = 'png') => {
    if (!result.result?.image_base64) return;
    const link = document.createElement('a');
    link.href = `data:image/${format};base64,${result.result.image_base64}`;
    link.download = `ai_generated_${Date.now()}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Image downloaded as ${format.toUpperCase()}`);
  };
  
  return (
    <div className={`rounded-xl p-3 my-2 border ${
      isSuccess 
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        {isSuccess ? (
          <CheckCircle className="w-5 h-5 text-green-600" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-red-600" />
        )}
        <span className={`font-medium ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
          {isSuccess ? 'Action Completed' : 'Action Failed'}
        </span>
      </div>
      
      {result.result && (
        <div className="text-sm text-slate-600 dark:text-slate-400">
          {result.result.message && <p>{result.result.message}</p>}
          {result.result.count !== undefined && (
            <p>Found <strong>{result.result.count}</strong> {result.result.entity}</p>
          )}
          {result.result.results && result.result.results.length > 0 && (
            <div className="mt-2 space-y-1">
              {result.result.results.slice(0, 3).map((item, idx) => (
                <div key={idx} className="bg-white dark:bg-slate-800 rounded px-2 py-1 text-xs">
                  {item.name || item.title || item.email || JSON.stringify(item).slice(0, 50)}
                </div>
              ))}
              {result.result.results.length > 3 && (
                <p className="text-xs text-slate-500">...and {result.result.results.length - 3} more</p>
              )}
            </div>
          )}
          {result.result.action === 'open_form' && (
            <p className="text-violet-600">→ Opening form for you...</p>
          )}
          
          {/* Image Result Display */}
          {result.result.image_base64 && (
            <div className="mt-3">
              <img 
                src={`data:image/png;base64,${result.result.image_base64}`}
                alt="Generated"
                className="w-full max-w-xs rounded-lg shadow-lg border border-slate-200 dark:border-slate-700"
              />
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadImage('png')}
                  className="gap-1 text-xs"
                >
                  <Download className="w-3 h-3" />
                  PNG
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadImage('jpg')}
                  className="gap-1 text-xs"
                >
                  <Download className="w-3 h-3" />
                  JPG
                </Button>
              </div>
            </div>
          )}
          
          {/* CRM Context Display - for need_details action */}
          {result.result.action === 'need_details' && (
            <div className="mt-3 space-y-2">
              <p className="text-violet-600 font-medium">📋 CRM Research Results:</p>
              {result.result.crm_context?.active_jobs && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Active Jobs:</p>
                  {result.result.crm_context.active_jobs.map((job, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-800 rounded px-2 py-1 text-xs mb-1">
                      {job.title} - {job.location || 'Remote'}
                    </div>
                  ))}
                </div>
              )}
              {result.result.crm_context?.suggestion && (
                <p className="text-xs text-violet-500 italic">{result.result.crm_context.suggestion}</p>
              )}
              {result.result.required_fields && (
                <p className="text-xs text-slate-500">
                  Required fields: <strong>{result.result.required_fields.join(', ')}</strong>
                </p>
              )}
            </div>
          )}
          
          {/* Select Record Display - for updates without ID */}
          {result.result.action === 'select_record' && result.result.records && (
            <div className="mt-3 space-y-2">
              <p className="text-amber-600 font-medium">🔍 Found {result.result.records.length} matching records:</p>
              {result.result.records.map((record, idx) => (
                <div key={idx} className="bg-white dark:bg-slate-800 rounded px-3 py-2 text-xs border border-slate-200 dark:border-slate-700">
                  <div className="font-medium">{record.name || record.title || record.subject}</div>
                  {record.email && <div className="text-slate-500">{record.email}</div>}
                  {record.status && <Badge variant="secondary" className="mt-1 text-xs">{record.status}</Badge>}
                  {record.id && <div className="text-slate-400 text-xs mt-1">ID: {record.id}</div>}
                </div>
              ))}
              <p className="text-xs text-slate-500 italic">
                Tip: Say "Update {result.result.entity} [ID] status to {result.result.new_status}" with the specific ID
              </p>
            </div>
          )}
          
          {/* Created Record Display */}
          {result.result.created && result.result.data && (
            <div className="mt-3">
              <p className="text-green-600 font-medium mb-2">✅ Created Successfully:</p>
              <div className="bg-white dark:bg-slate-800 rounded px-3 py-2 text-xs border border-green-200 dark:border-green-800">
                {Object.entries(result.result.data).slice(0, 5).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-0.5">
                    <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-slate-700 dark:text-slate-300">{String(value).slice(0, 30)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {result.error && (
        <p className="text-sm text-red-600">{result.error}</p>
      )}
    </div>
  );
};

// Capabilities Panel Component
const CapabilitiesPanel = ({ capabilities, onSelectExample }) => {
  return (
    <div className="space-y-3">
      <div className="text-center mb-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 mx-auto flex items-center justify-center mb-3 shadow-lg">
          <Command className="w-7 h-7 text-white" />
        </div>
        <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
          AI Agent Mode
        </h3>
        <p className="text-sm text-slate-500">
          I can execute actions in your CRM. Try these commands:
        </p>
      </div>
      
      {capabilities.map((cap, idx) => {
        const Icon = PROMPT_ICONS[cap.icon] || Zap;
        return (
          <div key={idx} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg ${ACTION_COLORS[cap.type] || 'bg-violet-500'} flex items-center justify-center`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900 dark:text-white">{cap.name}</p>
                <p className="text-xs text-slate-500">{cap.description}</p>
              </div>
            </div>
            <div className="space-y-1">
              {cap.examples.slice(0, 2).map((example, exIdx) => (
                <button
                  key={exIdx}
                  onClick={() => onSelectExample(example)}
                  className="w-full text-left text-xs px-2 py-1.5 rounded-lg bg-white dark:bg-slate-700 hover:bg-violet-50 dark:hover:bg-violet-900/30 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 flex items-center gap-1 group transition-colors"
                >
                  <ChevronRight className="w-3 h-3 text-slate-400 group-hover:text-violet-500" />
                  {example}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AIAssistant = ({ currentModule = 'general', isOpen, onClose, onOpen, onNavigate, onOpenImageGenerator }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [capabilities, setCapabilities] = useState([]);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  // AI Type Selection
  const [activeAI, setActiveAI] = useState('officium'); // 'officium' or 'crm'
  const [showTraining, setShowTraining] = useState(false);
  const [trainingExamples, setTrainingExamples] = useState([]);
  const [newTrainingExample, setNewTrainingExample] = useState({
    input: '',
    expected_action: '',
    expected_response: '',
    entity: '',
    notes: ''
  });
  
  // Draggable button position state
  const [buttonPosition, setButtonPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // Drag handlers for the floating button
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    const rect = dragRef.current.getBoundingClientRect();
    dragStartPos.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const newX = clientX - dragStartPos.current.x;
    const newY = clientY - dragStartPos.current.y;
    
    // Keep within viewport bounds
    const maxX = window.innerWidth - 180;
    const maxY = window.innerHeight - 60;
    
    setButtonPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging]);
  
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Add/remove drag event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  // Check voice support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!SpeechRecognition);
  }, []);
  
  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  // Voice recognition functions
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setIsListening(true);
      toast.info('Listening... Speak your command');
    };
    
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInputValue(transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else {
        toast.error('Voice recognition error. Please try again.');
      }
    };
    
    recognition.onend = () => {
      setIsListening(false);
      // Auto-send if we have text
      if (inputValue.trim()) {
        setTimeout(() => {
          if (inputRef.current?.value?.trim()) {
            sendMessage(inputRef.current.value);
          }
        }, 500);
      }
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };
  
  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };
  
  // Text-to-speech for AI responses
  const speakResponse = (text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Try to use a nice voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.name.includes('Google') || v.name.includes('Samantha')) || voices[0];
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    }
  };
  
  // Load capabilities
  useEffect(() => {
    const loadCapabilities = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API}/ai/agent/capabilities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCapabilities(response.data.capabilities || []);
      } catch (error) {
        console.error('Failed to load capabilities:', error);
      }
    };
    
    if (isOpen) {
      loadCapabilities();
    }
  }, [isOpen]);

  // Load training examples when training panel is opened
  useEffect(() => {
    const loadTrainingExamples = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API}/ai/crm-assistant/training`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrainingExamples(response.data.examples || []);
      } catch (error) {
        console.error('Failed to load training examples:', error);
      }
    };
    
    if (showTraining) {
      loadTrainingExamples();
    }
  }, [showTraining]);
  
  // Add training example
  const addTrainingExample = async () => {
    if (!newTrainingExample.input || !newTrainingExample.expected_action || !newTrainingExample.expected_response) {
      toast.error('Please fill in input, expected action, and expected response');
      return;
    }
    
    try {
      const token = getAuthToken();
      await axios.post(`${API}/ai/crm-assistant/training`, newTrainingExample, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training example added!');
      setNewTrainingExample({ input: '', expected_action: '', expected_response: '', entity: '', notes: '' });
      // Refresh list
      const response = await axios.get(`${API}/ai/crm-assistant/training`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingExamples(response.data.examples || []);
    } catch (error) {
      toast.error('Failed to add training example');
    }
  };
  
  // Delete training example
  const deleteTrainingExample = async (id) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/ai/crm-assistant/training/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training example deleted');
      setTrainingExamples(prev => prev.filter(ex => ex.id !== id));
    } catch (error) {
      toast.error('Failed to delete training example');
    }
  };
  
  // Load suggested prompts when module changes
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API}/ai/prompts/${currentModule}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuggestedPrompts(response.data.prompts || []);
      } catch (error) {
        console.error('Failed to load prompts:', error);
        setSuggestedPrompts([
          { text: "What can you help me with?", icon: "help" },
          { text: "Show me platform tips", icon: "lightbulb" }
        ]);
      }
    };
    
    if (isOpen) {
      loadPrompts();
    }
  }, [currentModule, isOpen]);
  
  // Load conversation history
  const loadConversations = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/ai/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };
  
  // Load a specific conversation
  const loadConversation = async (convId) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/ai/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages || []);
      setConversationId(convId);
      setShowHistory(false);
      setShowCapabilities(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    }
  };
  
  // Delete a conversation
  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/ai/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };
  
  // Start a new chat
  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
    setShowCapabilities(false);
    setPendingAction(null);
  };
  
  // Send message with agent parsing
  const sendMessage = async (messageText) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;
    
    setInputValue('');
    setIsLoading(true);
    setPendingAction(null);
    setShowCapabilities(false);
    setShowTraining(false);
    
    // Add user message immediately
    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const token = getAuthToken();
      
      if (activeAI === 'crm') {
        // CRM Assistant - Internal only, no web access
        const crmResponse = await axios.post(`${API}/ai/crm-assistant/chat`, {
          message: text,
          module: currentModule,
          conversation_history: messages.slice(-10).map(m => ({ role: m.role, content: m.content }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const assistantMessage = { 
          role: 'assistant', 
          content: crmResponse.data.reply,
          timestamp: new Date().toISOString(),
          aiType: 'crm',
          action: crmResponse.data.action
        };
        setMessages(prev => [...prev, assistantMessage]);
        
      } else {
        // Officium AI - Full capabilities with agent parsing
        const agentResponse = await axios.post(`${API}/ai/agent/parse`, {
          command: text,
          context: currentModule,
          conversation_id: conversationId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (agentResponse.data.action_detected && agentResponse.data.action) {
          // Action detected - show confirmation card
          const action = agentResponse.data.action;
          
          // Add assistant message with action
          const assistantMessage = { 
            role: 'assistant', 
            content: agentResponse.data.message,
            timestamp: new Date().toISOString(),
            hasAction: true,
            action: action,
            aiType: 'officium'
          };
          setMessages(prev => [...prev, assistantMessage]);
          setPendingAction(action);
          
        } else {
          // No action - regular AI response
          const assistantMessage = { 
            role: 'assistant', 
            content: agentResponse.data.message, 
            timestamp: new Date().toISOString(),
            aiType: 'officium'
          };
          setMessages(prev => [...prev, assistantMessage]);
        }
        
        if (agentResponse.data.conversation_id) {
          setConversationId(agentResponse.data.conversation_id);
        }
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.', 
        timestamp: new Date().toISOString(),
        isError: true 
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Execute pending action
  const executeAction = async () => {
    if (!pendingAction) return;
    
    setIsExecuting(true);
    
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API}/ai/agent/execute`, {
        action_id: pendingAction.action_id,
        action_type: pendingAction.action_type,
        target: pendingAction.target,
        operation: pendingAction.operation,
        parameters: pendingAction.parameters
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add result message
      const resultMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isResult: true,
        result: response.data,
        actionType: pendingAction.action_type
      };
      setMessages(prev => [...prev, resultMessage]);
      
      // Handle navigation actions
      if (pendingAction.action_type === 'navigate' && response.data.success && onNavigate) {
        const tab = response.data.result?.tab || pendingAction.parameters?.tab;
        if (tab) {
          onNavigate(tab);
          toast.success(`Navigated to ${tab}`);
        }
      }
      
      // Show success/error toast
      if (response.data.success) {
        toast.success('Action completed successfully!');
      } else {
        toast.error(response.data.error || 'Action failed');
      }
      
      setPendingAction(null);
      
    } catch (error) {
      console.error('Execute error:', error);
      toast.error('Failed to execute action');
      
      const errorMessage = {
        role: 'assistant',
        content: `Failed to execute action: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsExecuting(false);
    }
  };
  
  // Cancel pending action
  const cancelAction = () => {
    setPendingAction(null);
    const cancelMessage = {
      role: 'assistant',
      content: 'Action cancelled. Is there anything else I can help you with?',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, cancelMessage]);
  };
  
  // Copy message to clipboard
  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };
  
  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Get icon component for prompt
  const getPromptIcon = (iconName) => {
    const IconComponent = PROMPT_ICONS[iconName] || Sparkles;
    return <IconComponent className="w-3.5 h-3.5" />;
  };
  
  if (!isOpen) {
    const buttonStyle = buttonPosition.x !== null ? {
      left: buttonPosition.x,
      top: buttonPosition.y,
      right: 'auto',
      bottom: 'auto'
    } : {};
    
    return (
      <Button
        ref={dragRef}
        onClick={(e) => {
          if (!isDragging) onOpen();
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        className={`fixed h-12 sm:h-14 px-4 sm:px-5 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25 z-40 gap-2 transition-all ${isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab hover:scale-105'} ${buttonPosition.x === null ? 'bottom-20 sm:bottom-6 right-4 sm:right-6' : ''}`}
        style={buttonStyle}
        data-testid="ai-assistant-button"
        title="Drag to move • Click to open"
      >
        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="font-medium text-sm sm:text-base">Ask AI</span>
      </Button>
    );
  }
  
  return (
    <div className={`fixed bg-white dark:bg-slate-900 shadow-2xl z-[100] flex flex-col border-l border-slate-200 dark:border-slate-700 transition-all duration-300 ease-in-out ${
      isFullScreen 
        ? 'inset-0 w-full max-w-full' 
        : 'top-0 bottom-0 right-0 w-full sm:w-[420px]'
    }`} data-testid="ai-assistant-panel">
      {/* Header with Close Button */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-violet-600 to-purple-600 shrink-0">
        <div className="flex items-center gap-3">
          {(showHistory || showCapabilities || showTraining) ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setShowHistory(false); setShowCapabilities(false); setShowTraining(false); }}
              className="text-white hover:bg-white/20 h-8 w-8"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          ) : null}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full ${activeAI === 'crm' ? 'bg-emerald-500/30' : 'bg-white/20'} flex items-center justify-center`}>
              {activeAI === 'crm' ? <Database className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">
                {showHistory ? 'Chat History' : showCapabilities ? 'Agent Commands' : showTraining ? 'CRM Training' : activeAI === 'crm' ? 'CRM Assistant' : 'Officium AI'}
              </h3>
              <p className="text-xs text-white/70">
                {showHistory ? `${conversations.length} conversations` : showCapabilities ? 'What I can do' : showTraining ? 'Train CRM AI' : activeAI === 'crm' ? 'Internal CRM Only' : 'Open AI + Web Access'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!showHistory && !showCapabilities && !showTraining && (
            <>
              {/* AI Type Toggle */}
              <div className="flex items-center bg-white/10 rounded-full p-0.5 mr-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveAI('officium')}
                  className={`h-6 px-2 rounded-full text-xs ${activeAI === 'officium' ? 'bg-white text-violet-600' : 'text-white hover:bg-white/20'}`}
                  title="Officium AI - Open, Web-enabled"
                >
                  <Globe className="w-3 h-3 mr-1" />
                  Open
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveAI('crm')}
                  className={`h-6 px-2 rounded-full text-xs ${activeAI === 'crm' ? 'bg-white text-emerald-600' : 'text-white hover:bg-white/20'}`}
                  title="CRM Assistant - Internal only"
                >
                  <Database className="w-3 h-3 mr-1" />
                  CRM
                </Button>
              </div>
              {activeAI === 'crm' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setShowTraining(true); setShowHistory(false); setShowCapabilities(false); }}
                  className="text-white hover:bg-white/20 h-8 w-8"
                  title="Train CRM AI"
                >
                  <GraduationCap className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setShowCapabilities(true); setShowHistory(false); setShowTraining(false); }}
                className="text-white hover:bg-white/20 h-8 w-8"
                title="Agent Commands"
              >
                <Command className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { loadConversations(); setShowHistory(true); setShowCapabilities(false); setShowTraining(false); }}
                className="text-white hover:bg-white/20 h-8 w-8"
                title="Chat History"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={startNewChat}
                className="text-white hover:bg-white/20 h-8 w-8"
                title="New Chat"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </>
          )}
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="text-white hover:bg-white/20 h-8 w-8"
            title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
            data-testid="ai-fullscreen-button"
          >
            {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20 h-8 w-8 ml-1"
            title="Close"
            data-testid="ai-close-button"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Content */}
      {showHistory ? (
        /* History View */
        <ScrollArea className="flex-1 p-4">
          {conversations.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No conversations yet</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(false)}
                className="mt-4"
              >
                Start a conversation
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => loadConversation(conv.conversation_id)}
                  className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer group transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                        {conv.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {conv.last_message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {conv.module}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {conv.message_count} messages
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => deleteConversation(conv.conversation_id, e)}
                      className="opacity-0 group-hover:opacity-100 h-8 w-8 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      ) : showCapabilities ? (
        /* Capabilities View */
        <ScrollArea className="flex-1 p-4">
          <CapabilitiesPanel 
            capabilities={capabilities} 
            onSelectExample={(example) => {
              setShowCapabilities(false);
              setInputValue(example);
              inputRef.current?.focus();
            }}
          />
        </ScrollArea>
      ) : showTraining ? (
        /* Training Panel for CRM AI */
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mx-auto flex items-center justify-center mb-3 shadow-lg">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                Train CRM Assistant
              </h3>
              <p className="text-sm text-slate-500">
                Add examples to teach the CRM AI how to respond
              </p>
            </div>
            
            {/* Add New Example Form */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Training Example
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">User Input (what they might say)</label>
                  <Input
                    placeholder='e.g., "Add new lead from LinkedIn"'
                    value={newTrainingExample.input}
                    onChange={(e) => setNewTrainingExample(prev => ({ ...prev, input: e.target.value }))}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Expected Action</label>
                  <select
                    value={newTrainingExample.expected_action}
                    onChange={(e) => setNewTrainingExample(prev => ({ ...prev, expected_action: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  >
                    <option value="">Select action type...</option>
                    <option value="create_candidate">Create Candidate</option>
                    <option value="create_job">Create Job</option>
                    <option value="create_task">Create Task</option>
                    <option value="query_candidates">Query Candidates</option>
                    <option value="query_jobs">Query Jobs</option>
                    <option value="update_status">Update Status</option>
                    <option value="navigate">Navigate</option>
                    <option value="schedule_interview">Schedule Interview</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Expected Response</label>
                  <textarea
                    placeholder="How should the AI respond?"
                    value={newTrainingExample.expected_response}
                    onChange={(e) => setNewTrainingExample(prev => ({ ...prev, expected_response: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Entity (optional)</label>
                    <select
                      value={newTrainingExample.entity}
                      onChange={(e) => setNewTrainingExample(prev => ({ ...prev, entity: e.target.value }))}
                      className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                    >
                      <option value="">Select entity...</option>
                      <option value="candidates">Candidates</option>
                      <option value="jobs">Jobs</option>
                      <option value="tasks">Tasks</option>
                      <option value="tickets">Tickets</option>
                      <option value="applications">Applications</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Notes (optional)</label>
                    <Input
                      placeholder="Any notes..."
                      value={newTrainingExample.notes}
                      onChange={(e) => setNewTrainingExample(prev => ({ ...prev, notes: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                </div>
                <Button onClick={addTrainingExample} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4" />
                  Add Example
                </Button>
              </div>
            </div>
            
            {/* Existing Examples */}
            <div>
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Training Examples ({trainingExamples.length})
              </h4>
              {trainingExamples.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm">
                  No training examples yet. Add some above!
                </div>
              ) : (
                <div className="space-y-2">
                  {trainingExamples.map((example, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white">"{example.input}"</p>
                          <p className="text-xs text-violet-600 mt-1">→ {example.expected_action}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{example.expected_response}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteTrainingExample(example.id)}
                          className="h-7 w-7 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      ) : (
        /* Chat View */
        <>
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              /* Welcome State */
              <div className="py-6">
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg ${
                    activeAI === 'crm' 
                      ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/25' 
                      : 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-purple-500/25'
                  }`}>
                    {activeAI === 'crm' ? <Database className="w-8 h-8 text-white" /> : <Sparkles className="w-8 h-8 text-white" />}
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                    {activeAI === 'crm' ? 'CRM Assistant' : 'Officium AI'}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {activeAI === 'crm' 
                      ? 'Internal CRM operations only. No web access. Trainable!' 
                      : 'Open AI with web access, image generation, and more!'}
                  </p>
                  {activeAI === 'crm' && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                        <Database className="w-3 h-3 mr-1" />
                        Internal Only
                      </Badge>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <GraduationCap className="w-3 h-3 mr-1" />
                        Trainable
                      </Badge>
                    </div>
                  )}
                  {activeAI === 'officium' && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Badge variant="outline" className="text-violet-600 border-violet-300">
                        <Globe className="w-3 h-3 mr-1" />
                        Web Access
                      </Badge>
                      <Badge variant="outline" className="text-pink-600 border-pink-300">
                        <Palette className="w-3 h-3 mr-1" />
                        Images
                      </Badge>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCapabilities(true)}
                    className="mt-3 gap-2"
                  >
                    <Command className="w-4 h-4" />
                    View Commands
                  </Button>
                </div>
                
                {/* Suggested Prompts */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider px-1">
                    Try these commands
                  </p>
                  <div className="grid gap-2">
                    {[
                      { text: "Go to Candidates tab", icon: "compass" },
                      { text: "Find all open jobs", icon: "search" },
                      { text: "Show me pending tickets", icon: "list" },
                      { text: "How many applications today?", icon: "chart" },
                    ].map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => sendMessage(prompt.text)}
                        className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-violet-50 dark:hover:bg-slate-800 hover:border-violet-300 dark:hover:border-violet-500 text-left transition-all group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400 group-hover:bg-violet-200 dark:group-hover:bg-violet-900/50 transition-colors">
                          {getPromptIcon(prompt.icon)}
                        </div>
                        <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">
                          {prompt.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div key={index}>
                    <div className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                        {!message.isResult && (
                          <div
                            className={`rounded-2xl px-4 py-3 ${
                              message.role === 'user'
                                ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                                : message.isError
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                            }`}
                          >
                            <div 
                              className="text-sm whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{ __html: formatAIMessage(message.content) }}
                            />
                          </div>
                        )}
                        
                        {/* Action Confirmation Card */}
                        {message.hasAction && message.action && pendingAction?.action_id === message.action.action_id && (
                          <ActionConfirmCard
                            action={message.action}
                            onExecute={executeAction}
                            onCancel={cancelAction}
                            isExecuting={isExecuting}
                          />
                        )}
                        
                        {/* Action Result Card */}
                        {message.isResult && message.result && (
                          <ActionResultCard
                            result={message.result}
                            actionType={message.actionType}
                          />
                        )}
                        
                        {message.role === 'assistant' && !message.isResult && !message.hasAction && (
                          <div className="flex items-center gap-1 mt-1 ml-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyToClipboard(message.content, index)}
                              className="h-6 w-6 text-slate-400 hover:text-slate-600"
                            >
                              {copiedIndex === index ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
          
          {/* Quick Actions (show after conversation started) */}
          {messages.length > 0 && !isLoading && !pendingAction && (
            <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800">
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {[
                  { text: "Go to Candidates", icon: "compass" },
                  { text: "Show open tickets", icon: "search" },
                  { text: "Create new task", icon: "plus" },
                ].map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => sendMessage(prompt.text)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-violet-100 dark:hover:bg-violet-900/30 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap transition-colors"
                  >
                    {getPromptIcon(prompt.icon)}
                    <span>{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Input */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div className="flex items-end gap-2">
              {/* Voice Button */}
              {voiceSupported && (
                <Button
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading || isExecuting}
                  variant={isListening ? "default" : "outline"}
                  className={`h-11 w-11 rounded-xl p-0 ${isListening ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : ''}`}
                  title={isListening ? "Stop listening" : "Voice input"}
                  data-testid="voice-button"
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
              )}
              
              {/* Image Generator Button */}
              {onOpenImageGenerator && (
                <Button
                  onClick={onOpenImageGenerator}
                  disabled={isLoading || isExecuting}
                  variant="outline"
                  className="h-11 w-11 rounded-xl p-0"
                  title="AI Image Generator"
                  data-testid="image-generator-button"
                >
                  <ImageIcon className="w-5 h-5" />
                </Button>
              )}
              
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={isListening ? "Listening..." : "Type a command or ask a question..."}
                  className="pr-12 py-3 rounded-xl border-slate-200 dark:border-slate-700 focus:border-violet-400 focus:ring-violet-400"
                  disabled={isLoading || isExecuting || isListening}
                  data-testid="ai-input"
                />
              </div>
              <Button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading || isExecuting}
                className="h-11 w-11 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 p-0"
                data-testid="ai-send-button"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              {isListening ? '🎤 Listening... speak your command' : 'Voice & AI Image Generation enabled • All actions require confirmation'}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAssistant;
